// src/components/League/backing/PodList.jsx
//
// Backing Beta PR 4 — SURFACE A, the upcoming pods (spec V1.3 §5 "Upcoming
// pods"; Amendment B §B2/§B6 the open-state contract; design brief §2.A, rev2
// §4 "show it populated the way week one will actually look"). Rendered from
// GET /api/tournament/backing-pools and nothing else.
//
// THE OPEN-STATE CONTRACT, rendered exactly and only (Amendment B §B2):
//   · backers n of 3 — the three chairs, from the API's ALREADY-CAPPED
//     `backerProgress` (the endpoint caps; this file never re-derives a count);
//   · team spread met / unmet — the API's boolean;
//   · both frozen once qualified — the BackersCall's frozen state, the API's
//     `met` pair;
//   · the viewer's own stakes — `myStakes`, summed for the §B6 line;
//   · SEALED lockups where the pot and pays × will sit at close.
// A CLOSED / INSUFFICIENT / RESOLVED pool renders the revealed view the
// endpoint returns: the pot, the exact backer count, per-team totals and
// counts, pays × where settlement wrote it.
//
// THE VIEWER'S OWN POD READS AS YOURS, NOT DISABLED (brief §2.A): a "Yours"
// tag on the pod, every seat still opens its card (the card's CTA says the
// pod is yours), nothing greyed out.
//
// NO POT, SHARE, PAYOUT, COUNT ABOVE THREE OR ACTIVITY INDICATOR appears for
// an open pool — the seal test (PodList.test.jsx) renders a pool whose object
// has been given a pot and asserts the figure never reaches the DOM.
//
// "FULL" (Amendment D R2) is the one size fact: an open pool whose document
// says `full`, shown to a viewer with NO stake in it (`poolFullForViewer`) —
// a word, never a count. Its seats still open their cards, and the card's
// call to back reads "This pool is full." (TeamCard's BackButton) — no way
// to start a stake there. A viewer who holds a stake sees their pod as today.
//
// The design's `backing-browse.jsx` was not in the attached bundle (the
// landing-door canvas carries the strip, card, parts and data modules); this
// list is built from the briefs' contract with the bundle's parts vocabulary
// (Sealed, Chairs, BackersCall) and the League's PodCard row rhythm.
//
// EVERY SEAT IS NAMED BY THE SERVER (Amendment C §C1, D-af): the row shows the
// team's `label` — its primary agent's name — with the player's display name
// (`secondary`) on the line beneath where the server sent one. Nothing here
// composes a name from an id; a seat without a label reads "Unnamed team".
//
// THE DESKTOP LEFT COLUMN reuses this list whole (Backing desktop layouts):
// the same rows, the same seal, the same order the endpoint sends — a wider
// screen shows more at once, never more about a pool. The one addition is
// `selectedSeat` — the seat whose card is open in the centre column — marked
// on its row (the viewer's own navigation, not a pool fact). Mobile passes
// nothing: the markup main ships (backingMobilePin.test.jsx).

import React from 'react';
import { POOL_STRIP } from '../../../constants/backing';
import { LTOKENS, LX, alpha } from '../leagueTokens';
import { Eyebrow, Mono, Icon, LIcon, Tag, AgentAvatar, KindMark } from '../LeagueParts';
import { baseGroupName, seatColor } from '../leagueAdapter';
import { BackersCall, MonoAttr, Sealed } from './BackingParts';
import { POD_LIST, bp } from './backingCopy';
import { liveStakeTotal, poolFullForViewer } from './backingStakes';
import { formatEtClose, teamLabelOf } from './backingStripState';

const REVEALED = new Set(['closed', 'insufficient', 'resolving', 'resolved', 'refunded']);

function SeatRow({ pod, team, revealed, onOpenSeat, accent, selected = false }) {
  const name = teamLabelOf(team);
  const secondary = typeof team.secondary === 'string' && team.secondary.length > 0 ? team.secondary : null;
  const agent = { kind: team.isCpu ? 'cpu' : 'human', color: seatColor(team.odUserId, team.isCpu), you: team.isOwnSeat };
  const pays = revealed ? POD_LIST.revealed.pays(team.paysX) : null;
  return (
    <div
      className="lg-tap"
      role="button"
      tabIndex={0}
      data-backing="seat"
      aria-current={selected ? 'true' : undefined}
      onClick={() => onOpenSeat?.(pod, team)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenSeat?.(pod, team); } }}
      style={selected
        ? { display: 'flex', alignItems: 'center', gap: 11, padding: '9px 8px', margin: '0 -6px', cursor: 'pointer', borderRadius: 11, background: alpha(accent, 0.09), boxShadow: `inset 2px 0 0 ${accent}` }
        : { display: 'flex', alignItems: 'center', gap: 11, padding: '9px 2px', cursor: 'pointer' }}
    >
      <AgentAvatar agent={agent} size={32} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <span style={{ fontSize: 13.5, fontWeight: 700, color: LTOKENS.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
          {team.isOwnSeat && <Tag color={accent}>You</Tag>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 1, minWidth: 0 }}>
          {secondary && <span data-backing="seat-secondary" style={{ fontSize: 11, color: LTOKENS.ink2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{secondary}</span>}
          {team.isCpu ? <KindMark agent={agent} /> : <Mono style={{ fontSize: 10, color: LTOKENS.ink3 }}>{POD_LIST.humanMark}</Mono>}
        </div>
      </div>
      {revealed && Number.isFinite(team.stakeTotal) && Number.isFinite(team.backerCount) && (
        <div style={{ textAlign: 'right' }}>
          <Mono style={{ fontSize: 10.5, color: LTOKENS.ink2, display: 'block' }}>{POD_LIST.revealed.team(team.stakeTotal, team.backerCount)}</Mono>
          {pays && <Mono style={{ fontSize: 10, color: LTOKENS.gold, display: 'block' }}>{pays}</Mono>}
        </div>
      )}
      <Icon name="chevR" size={14} color={LTOKENS.ink3} />
    </div>
  );
}

export function PodEntry({ pod, onOpenSeat, accent = LX.energy, selectedSeat = null }) {
  const pool = pod.pool ?? null;
  const open = pool?.status === 'open';
  const revealed = pool != null && REVEALED.has(pool.status);
  const yours = pod.teams.some((t) => t.isOwnSeat);
  const myTotal = liveStakeTotal(pod);
  // Amendment D R2: "Full" to a viewer with no stake here; a backer's pod
  // reads as before (their stake line, a top-up still open to them).
  const full = poolFullForViewer(pod);
  const humans = pod.teams.filter((t) => !t.isCpu).length;
  const cpus = pod.teams.length - humans;
  const closeLabel = formatEtClose(pool?.closesAt);

  return (
    <div
      data-backing="pod"
      data-pool-status={pool?.status ?? 'none'}
      style={{
        borderRadius: 18, padding: '13px 14px',
        background: yours ? `linear-gradient(165deg, ${alpha(accent, 0.06)}, ${LTOKENS.surface} 58%)` : LTOKENS.surface,
        border: `1px solid ${yours ? alpha(accent, 0.26) : LTOKENS.hair}`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: LTOKENS.ink, letterSpacing: '-0.01em' }}>{baseGroupName(pod.groupId)}</span>
          <Tag color={pod.formationPath === 'slot' ? LX.cpu : LTOKENS.ink3}>{pod.formationPath === 'slot' ? POD_LIST.slot : POD_LIST.lobby}</Tag>
          {yours && <Tag color={accent}>{POD_LIST.yours}</Tag>}
          {full && <span data-backing="pool-full"><Tag color={LTOKENS.ink3}>{POD_LIST.full}</Tag></span>}
        </div>
        <Mono style={{ fontSize: 9.5, color: LTOKENS.ink3, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <Icon name="clock" size={10} color={LTOKENS.ink3} />
          {open ? POD_LIST.closes(closeLabel) : revealed ? POD_LIST.status[pool.status] : POD_LIST.noPool}
        </Mono>
      </div>

      <Mono style={{ fontSize: 9.5, color: LTOKENS.ink3, letterSpacing: '0.06em', display: 'block', marginBottom: 4 }}>{POD_LIST.seats(humans, cpus)}</Mono>

      <div>
        {pod.teams.map((team) => (
          <SeatRow key={team.odUserId} pod={pod} team={team} revealed={revealed} onOpenSeat={onOpenSeat} accent={accent} selected={selectedSeat != null && selectedSeat.groupId === pod.groupId && selectedSeat.odUserId === team.odUserId} />
        ))}
      </div>

      <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${LTOKENS.hair}`, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {open && (
          <>
            <BackersCall progress={pool.backerProgress} spread={pool.teamSpread} compact />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <Sealed label={POD_LIST.sealedPot} size="sm" />
              <Sealed label={POD_LIST.sealedPays} size="sm" />
              {myTotal > 0 && (
                <MonoAttr data-backing="your-backing" style={{ marginLeft: 'auto', fontSize: 10.5, color: accent, fontWeight: 600 }}>
                  {POOL_STRIP.yourBacking.replace('{amount}', bp(myTotal))}
                </MonoAttr>
              )}
            </div>
          </>
        )}
        {revealed && (
          <div data-backing="revealed" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {/* Figures only where the document carries them (a refund over a
                missing pod folds to zeros that are not a pot) — never a default. */}
            {pool.status !== 'refunded' && Number.isFinite(pool.potTotal) && <Mono style={{ fontSize: 11.5, fontWeight: 700, color: LTOKENS.ink }}>{POD_LIST.revealed.pot(pool.potTotal)}</Mono>}
            {pool.status !== 'refunded' && Number.isFinite(pool.uniqueBackers) && <Mono style={{ fontSize: 10.5, color: LTOKENS.ink2 }}>{POD_LIST.revealed.backers(pool.uniqueBackers)}</Mono>}
            {myTotal > 0 && (
              <MonoAttr data-backing="your-backing" style={{ marginLeft: 'auto', fontSize: 10.5, color: accent, fontWeight: 600 }}>
                {POOL_STRIP.yourBacking.replace('{amount}', bp(myTotal))}
              </MonoAttr>
            )}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <LIcon name="eyeR" size={13} color={accent} />
          <Mono style={{ fontSize: 10.5, color: accent, fontWeight: 600, letterSpacing: '0.04em' }}>{POD_LIST.tapSeat}</Mono>
        </div>
      </div>
    </div>
  );
}

export default function PodList({ pods, onOpenSeat, accent = LX.energy, selectedSeat = null }) {
  const list = Array.isArray(pods) ? pods : [];
  if (list.length === 0) {
    return (
      <div data-backing="pod-list-empty" style={{ padding: '15px 14px', borderRadius: 13, background: LTOKENS.surface, border: `1px dashed ${LTOKENS.hair2}`, textAlign: 'center' }}>
        <Mono style={{ fontSize: 11, color: LTOKENS.ink3, lineHeight: 1.5 }}>{POD_LIST.empty}</Mono>
      </div>
    );
  }
  return (
    <div data-backing="pod-list" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Eyebrow color={LTOKENS.ink3}>{POD_LIST.title}</Eyebrow>
      {list.map((pod) => <PodEntry key={pod.groupId} pod={pod} onOpenSeat={onOpenSeat} accent={accent} selectedSeat={selectedSeat} />)}
    </div>
  );
}
